import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../api';
import EventDetailsModal from '../components/EventDetailsModal';

const EventPreviewCard = ({ e, onClick }) => {
  return (
    <div
      className="event-preview-card"
      onClick={() => onClick(e._id)}
      role="button"
      tabIndex={0}
      onKeyDown={(ev) => ev.key === 'Enter' && onClick(e._id)}
    >
      <div className="event-date-block">
        <div className="event-date-day" style={e.isMultiDay ? { fontSize: '0.92rem' } : {}}>
          {e.isMultiDay && e.endDate ? (
            (() => {
              const start = new Date(e.date);
              const end = new Date(e.endDate);
              if (start.getMonth() === end.getMonth() && start.getFullYear() === end.getFullYear()) {
                return `${start.getDate()}-${end.getDate()}`;
              }
              return `${start.getDate()}…${end.getDate()}`;
            })()
          ) : new Date(e.date).getDate()}
        </div>
        <div className="event-date-month" style={e.isMultiDay && e.endDate && new Date(e.date).getMonth() !== new Date(e.endDate).getMonth() ? { fontSize: '0.55rem', letterSpacing: '0' } : {}}>
          {(() => {
            const start = new Date(e.date);
            const end = e.endDate ? new Date(e.endDate) : null;
            const startMonth = start.toLocaleDateString('en-US', { month: 'short' }).toUpperCase();
            if (e.isMultiDay && end && start.getMonth() !== end.getMonth()) {
              const endMonth = end.toLocaleDateString('en-US', { month: 'short' }).toUpperCase();
              return `${startMonth}-${endMonth}`;
            }
            return startMonth;
          })()}
        </div>
      </div>
      <div className="event-preview-content">
        <div className="event-preview-title-row">
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.625rem', flexWrap: 'wrap' }}>
            <div className="event-preview-name">{e.title}</div>
            {e.eventMode !== 'online' && (
              <span className="meta-pill venue-pill">{e.venue || 'TBD'}</span>
            )}
            <span className={`meta-pill mode-pill ${e.eventMode}`}>
              {e.eventMode || 'Offline'}
            </span>
          </div>
          <div className="event-type-badge">{e.type}</div>
        </div>
        
        <div className="event-preview-meta-v2">
          <div className="meta-line secondary">
            <span className="host-info">Hosted by <strong>{e.faculty?.name || 'Faculty'}</strong></span>
            <span className="dot">·</span>
            <span className="guest-count">{e.passCount === 1 ? 'Guest' : 'Guests'}: <strong>{e.passCount || 0}</strong></span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default function IQACDashboard() {
  const { faculty } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({ 
    eventsConducted: 0, 
    upcomingEvents: 0, 
    guestsArrived: 0, 
    upcomingGuests: 0,
    entriesToday: 0,
    pendingEntriesToday: 0
  });
  const [upcomingEventsList, setUpcomingEventsList] = useState([]);
  const [conductedEventsList, setConductedEventsList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedEventId, setSelectedEventId] = useState(null);

  const isAdmin = ['iqac', 'super_admin'].includes(faculty?.role);
  const isAO = faculty?.role === 'ao';
  const dashboardTitle = isAdmin ? 'IQAC Admin · System Dashboard' : (isAO ? 'AO · Administrative Dashboard' : 'Faculty Portal · Personal Dashboard');
  const eventsUrl = (isAdmin || isAO) ? '/events/all' : '/events';

  useEffect(() => {
    Promise.all([
      api.get(eventsUrl),
      api.get('/passes'),
    ]).then(([evRes, passRes]) => {
      const events = evRes.data.events || [];
      const passes = passRes.data.passes || [];
      const todayStart = new Date();
      todayStart.setHours(0, 0, 0, 0);

      // Calculate KPIs
      const upcomingEvents = events.filter(e => {
        const eventEnd = e.isMultiDay && e.endDate ? new Date(e.endDate) : new Date(e.date);
        const eventEndDay = new Date(eventEnd.getFullYear(), eventEnd.getMonth(), eventEnd.getDate());
        return eventEndDay >= todayStart;
      });

      const conductedEvents = events.filter(e => {
        const eventEnd = e.isMultiDay && e.endDate ? new Date(e.endDate) : new Date(e.date);
        const eventEndDay = new Date(eventEnd.getFullYear(), eventEnd.getMonth(), eventEnd.getDate());
        return eventEndDay < todayStart;
      });

      const conductedCount = conductedEvents.length;
      
      const arrived = passes.filter(p => p.status === 'used').length;
      const upcomingPass = passes.filter(p => p.status === 'active' || p.status === 'pending').length;

      const isToday = (d) => {
        if (!d) return false;
        const dt = new Date(d);
        const today = new Date();
        return dt.toDateString() === today.toDateString();
      };

      const isEventActiveToday = (e) => {
        if (!e || !e.date) return false;
        const start = new Date(e.date);
        const end = e.isMultiDay && e.endDate ? new Date(e.endDate) : start;
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const s = new Date(start.getFullYear(), start.getMonth(), start.getDate());
        const e_date = new Date(end.getFullYear(), end.getMonth(), end.getDate());
        return today >= s && today <= e_date;
      };

      const entriesToday = passes.filter(p => p.status === 'used' && isToday(p.entryTime)).length;
      const pendingEntriesToday = passes.filter(p => {
        const pStatus = p.status || 'pending';
        const isPending = pStatus === 'active' || pStatus === 'pending';
        return isPending && isEventActiveToday(p.event);
      }).length;

      setStats({
        eventsConducted: conductedCount,
        upcomingEvents: upcomingEvents.length,
        guestsArrived: arrived,
        upcomingGuests: upcomingPass,
        entriesToday,
        pendingEntriesToday
      });

      // Get upcoming events for the card
      setUpcomingEventsList(upcomingEvents
        .sort((a, b) => new Date(a.date) - new Date(b.date))
        .slice(0, 5)
      );

      // Get conducted events for the card
      setConductedEventsList(conductedEvents
        .sort((a, b) => new Date(b.date) - new Date(a.date)) // Latest first
        .slice(0, 5)
      );
    }).catch(err => {
      console.error('Error loading dashboard:', err);
    }).finally(() => setLoading(false));
  }, [eventsUrl]);

  const hour = new Date().getHours();
  const greeting = hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening';

  if (loading) {
    return (
      <div className="page-loading">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="page-shell">
      <div className="page-header">
        <div>
          <div className="page-title">{greeting}, {faculty?.name?.split(' ')[0]}</div>
          <div className="page-subtitle">{dashboardTitle}</div>
        </div>
        <div className="page-actions">
          {!isAO && (
            <>
              <button type="button" className="btn btn-ghost" onClick={() => navigate(isAdmin ? '/admin/events' : '/faculty/events')}>New event</button>
              <button type="button" className="btn btn-navy" onClick={() => navigate(isAdmin ? '/admin/guest-list' : '/faculty/guest-list')}>Add Guest</button>
            </>
          )}
        </div>
      </div>

      <div className="page-body">
        {/* Stats Grid - 4 KPIs */}
        <div className="stats-grid" style={{ marginBottom: '1.5rem' }}>
          {(() => {
            const aoStats = [
              { label: 'Upcoming Events', value: stats.upcomingEvents },
              { label: 'Active Passes', value: stats.upcomingGuests },
              { label: 'Entries Today', value: stats.entriesToday },
              { label: 'Pending Entries Today', value: stats.pendingEntriesToday }
            ];

            const defaultStats = [
              { label: 'Events Conducted', value: stats.eventsConducted },
              { label: 'Upcoming Events', value: stats.upcomingEvents },
              { label: 'Guests Arrived', value: stats.guestsArrived },
              { label: 'Upcoming Guests', value: stats.upcomingGuests }
            ];

            return (isAO ? aoStats : defaultStats).map(s => (
              <div 
                className="stat-card" 
                key={s.label}
              >
                <div className="stat-value">{s.value}</div>
                <div className="stat-label">{s.label}</div>
              </div>
            ));
          })()}
        </div>

        <div className="grid-2">
          {/* Upcoming Events Card */}
          <div className="card">
            <div className="card-head">
              <h2 className="card-heading">Upcoming Events</h2>
              <button type="button" className="btn btn-ghost btn-sm" onClick={() => navigate(isAdmin ? '/admin/events' : '/faculty/events')}>
                View all
              </button>
            </div>
            {upcomingEventsList.length === 0 ? (
              <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-3)' }}>
                <div style={{ fontSize: '0.875rem', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 700 }}>
                  NOTHING HERE
                </div>
                <div style={{ fontSize: '0.9375rem', marginTop: '0.5rem' }}>
                  No upcoming events
                </div>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.625rem' }}>
                {upcomingEventsList.map(e => (
                  <EventPreviewCard key={e._id} e={e} onClick={setSelectedEventId} />
                ))}
              </div>
            )}
          </div>

          {/* Recently Conducted Events Card */}
          <div className="card">
            <div className="card-head">
              <h2 className="card-heading">Recently Conducted</h2>
              <button type="button" className="btn btn-ghost btn-sm" onClick={() => navigate(isAdmin ? '/admin/events?tab=conducted' : '/faculty/events?tab=conducted')}>
                View all
              </button>
            </div>
            {conductedEventsList.length === 0 ? (
              <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-3)' }}>
                <div style={{ fontSize: '0.875rem', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 700 }}>
                  NOTHING HERE
                </div>
                <div style={{ fontSize: '0.9375rem', marginTop: '0.5rem' }}>
                  No conducted events
                </div>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.625rem' }}>
                {conductedEventsList.map(e => (
                  <EventPreviewCard key={e._id} e={e} onClick={setSelectedEventId} />
                ))}
              </div>
            )}
          </div>
        </div>

        {selectedEventId && <EventDetailsModal eventId={selectedEventId} onClose={() => setSelectedEventId(null)} />}
      </div>
    </div>
  );
}
